import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowUpRight, MapPin } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/brand";
import { MaskLine } from "./Reveal";

export default function Hero() {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 100]);
  const imgScale = useTransform(scrollY, [0, 800], [1, 1.06]);
  const textY = useTransform(scrollY, [0, 800], [0, -40]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] bg-[#FFFFFF] text-[#111613] overflow-hidden grain"
    >
      {/* Subtle warm ambient */}
      <div className="absolute -top-32 left-1/4 w-[500px] h-[500px] bg-[#DA7B3C]/8 blur-[160px] rounded-full pointer-events-none" />
      <div className="absolute -bottom-32 right-0 w-[500px] h-[500px] bg-[#0E2A1A]/10 blur-[160px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 pt-28 md:pt-32 pb-16 min-h-[100vh] flex flex-col">
        {/* Top meta row */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="flex flex-wrap items-center justify-between gap-4 pb-6 md:pb-10 border-b border-[#111613]/15"
        >
          <div className="chapter-line text-[#0E2A1A]">
            Est. 2000 · San Antonio, TX
          </div>
          <div className="hidden md:flex items-center gap-6 font-mono-sm text-[#6B7A6E]">
            <span>N°01 · Family-Owned</span>
            <span className="w-6 h-px bg-[#111613]/20" />
            <span>Licensed & Insured</span>
          </div>
        </motion.div>

        {/* Main hero grid */}
        <motion.div
          style={{ y: textY }}
          className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 pt-14 md:pt-20 items-center"
        >
          {/* Left: text */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="chapter-line text-[#DA7B3C] mb-8"
            >
              Landscaping · Junk Removal · Bobcat
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="font-display text-[#0E2A1A] text-[14vw] sm:text-[11vw] md:text-[9vw] lg:text-[8.2vw] xl:text-[7.6vw] leading-[0.92] tracking-[-0.025em]"
              style={{ fontWeight: 400 }}
            >
              <MaskLine delay={0.5}>San Antonio</MaskLine>
              <MaskLine delay={0.68}>
                <span className="italic text-[#DA7B3C]">landscaping</span>
              </MaskLine>
              <MaskLine delay={0.86}>
                <span className="deco-amp">&amp;</span> junk removal
              </MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9 }}
              className="mt-10 md:mt-12 text-lg md:text-xl text-[#111613]/75 max-w-xl leading-relaxed"
            >
              {"Dirt moved. Grass cut. Junk hauled. Driveways laid. Whatever your yard needs — we've been doing it in San Antonio for 25 years."}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.9 }}
              className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-3 md:gap-4"
            >
              <a
                href="#quote"
                data-testid="hero-cta-quote"
                className="btn-primary"
              >
                Get A Free Quote
                <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
              </a>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="hero-cta-call"
                className="btn-ghost"
              >
                <Phone className="w-4 h-4" strokeWidth={2} />
                {BRAND.phone}
              </a>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 1 }}
              className="mt-16 pt-8 border-t border-[#111613]/15 grid grid-cols-3 gap-4 md:gap-10 max-w-xl"
            >
              <div>
                <div className="font-display text-5xl md:text-6xl text-[#0E2A1A] leading-none">
                  25<span className="text-[#DA7B3C]">+</span>
                </div>
                <div className="mt-3 font-mono-sm text-[#6B7A6E]">
                  Years working
                </div>
              </div>
              <div>
                <div className="font-display text-5xl md:text-6xl text-[#0E2A1A] leading-none">
                  7
                </div>
                <div className="mt-3 font-mono-sm text-[#6B7A6E]">
                  Core services
                </div>
              </div>
              <div>
                <div className="font-display text-5xl md:text-6xl text-[#0E2A1A] leading-none">
                  SA<span className="text-[#DA7B3C]">·</span>TX
                </div>
                <div className="mt-3 font-mono-sm text-[#6B7A6E]">
                  & surrounding
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: hero image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src={IMAGES.hero}
                alt="Freshly landscaped front yard by El Monos"
                className="w-full h-full object-cover will-change-transform"
              />
              {/* Framed detail */}
              <div className="absolute inset-3 border border-[#FFFFFF]/40 pointer-events-none" />
            </div>
            {/* Caption */}
            <div className="mt-4 flex items-center justify-between font-mono-sm text-[#6B7A6E]">
              <span>Recent Work · San Antonio</span>
              <span className="inline-flex items-center gap-2">
                <MapPin className="w-3 h-3 text-[#DA7B3C]" strokeWidth={2} />
                East Side
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-10 flex items-center gap-3 font-mono-sm text-[#6B7A6E]"
        >
          <span className="block w-10 h-px bg-[#DA7B3C]" />
          Scroll to explore
        </motion.div>
      </div>
    </section>
  );
}
